import { EyeOff, Send, UserRound } from "lucide-react";
import { useRef, useState, type FormEvent } from "react";

import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Textarea } from "components/ui/textarea";
import { cn } from "utils";

import { answerErrors, cleanAnswers } from "./logic";
import {
  LIMITS,
  type Answer,
  type Answers,
  type FeedbackForm,
  type FeedbackQuestion,
} from "./schemas";

export const ANONYMOUS_NOTICE =
  "Organisers will not see who answered. Anonymous answers can't be changed after you submit.";
export const NAMED_NOTICE =
  "Organisers will see your name with your answers. You can change them until the form closes.";

type FormContent = Pick<
  FeedbackForm,
  "title" | "intro" | "anonymous" | "questions"
>;

/**
 * The form as an attendee fills it in. The organiser's preview renders the
 * same component with `preview`, so what they check is what attendees get.
 */
export function FeedbackFormView({
  form,
  initialAnswers,
  preview = false,
  disabled = false,
  submitting = false,
  submitLabel = "Submit feedback",
  onSubmit,
}: {
  form: FormContent;
  initialAnswers?: Answers;
  preview?: boolean;
  disabled?: boolean;
  submitting?: boolean;
  submitLabel?: string;
  onSubmit?: (answers: Answers) => void;
}) {
  const [answers, setAnswers] = useState<Answers>(initialAnswers ?? {});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const questionRefs = useRef(new Map<string, HTMLFieldSetElement>());

  const setAnswer = (questionId: string, answer: Answer | undefined) => {
    setAnswers((current) => {
      const next = { ...current };
      if (answer === undefined) delete next[questionId];
      else next[questionId] = answer;
      return next;
    });
    setErrors((current) => {
      if (!(questionId in current)) return current;
      const next = { ...current };
      delete next[questionId];
      return next;
    });
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (preview || disabled || submitting) return;
    const found = answerErrors(form.questions, answers);
    setErrors(found);
    const first = form.questions.find((question) => question.id in found);
    if (first) {
      const fieldset = questionRefs.current.get(first.id);
      fieldset?.querySelector<HTMLElement>("input, textarea, button")?.focus();
      return;
    }
    onSubmit?.(cleanAnswers(form.questions, answers));
  };

  const locked = disabled || submitting;

  return (
    <form
      noValidate
      onSubmit={submit}
      className="min-w-0 space-y-6"
      data-testid="feedback-form"
    >
      {/* The same dark band as the programme's day header, so the form opens
          like the rest of the event pages. */}
      <div
        className="min-w-0 space-y-2 rounded-2xl border border-border bg-dusk-hero px-5 py-6 text-white dark:border-edge dark:border-lit-edge"
        data-testid="feedback-header"
      >
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-white/80">
          Feedback
        </p>
        <h2 className="font-display text-3xl font-normal leading-tight text-white wrap-anywhere">
          {form.title || "Untitled form"}
        </h2>
        {form.intro && (
          <p className="whitespace-pre-line text-sm text-white/85 wrap-anywhere">
            {form.intro}
          </p>
        )}
      </div>
      <div className="min-w-0 space-y-2">
        <p
          className="flex items-start gap-2 rounded-lg border border-border bg-background/50 px-3 py-2 text-sm text-foreground"
          data-testid="feedback-identity-notice"
        >
          {form.anonymous ? (
            <EyeOff className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          ) : (
            <UserRound className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          )}
          <span className="min-w-0">
            {form.anonymous ? ANONYMOUS_NOTICE : NAMED_NOTICE}
          </span>
        </p>
      </div>

      {form.questions.map((question, index) => (
        <QuestionField
          key={question.id}
          ref={(element) => {
            if (element) questionRefs.current.set(question.id, element);
            else questionRefs.current.delete(question.id);
          }}
          number={index + 1}
          question={question}
          answer={answers[question.id]}
          error={errors[question.id]}
          disabled={locked}
          onChange={(answer) => setAnswer(question.id, answer)}
        />
      ))}

      {Object.keys(errors).length > 0 && (
        <p role="alert" className="text-sm font-medium text-hb-red-text">
          Some required questions are not answered yet.
        </p>
      )}

      <Button
        variant="cta"
        type="submit"
        className="h-11 w-full sm:w-auto"
        disabled={preview || locked}
        data-testid="feedback-submit"
      >
        <Send className="h-4 w-4" />
        {preview
          ? "Submit (disabled in preview)"
          : submitting
            ? "Sending…"
            : submitLabel}
      </Button>
    </form>
  );
}

function QuestionField({
  ref,
  number,
  question,
  answer,
  error,
  disabled,
  onChange,
}: {
  ref: (element: HTMLFieldSetElement | null) => void;
  number: number;
  question: FeedbackQuestion;
  answer: Answer | undefined;
  error: string | undefined;
  disabled: boolean;
  onChange: (answer: Answer | undefined) => void;
}) {
  const errorId = `feedback-error-${question.id}`;
  const labelId = `feedback-label-${question.id}`;

  return (
    <fieldset
      ref={ref}
      className="min-w-0 space-y-3 rounded-2xl border border-border bg-background/60 p-4 [&>legend+*]:clear-both"
      aria-describedby={error ? errorId : undefined}
      aria-invalid={error ? true : undefined}
      data-testid={`feedback-question-${number}`}
    >
      <legend
        id={labelId}
        // Floated, so the label sits inside the card and not on its top border,
        // which is where a fieldset draws a legend.
        className="float-left mb-1 w-full font-medium text-foreground wrap-anywhere"
      >
        <span
          className="mr-2 inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-hb-purple-fill px-2 align-middle text-xs font-bold text-white"
          aria-hidden="true"
        >
          {number}
        </span>
        <span className="sr-only">{number}. </span>
        {question.label}
        {question.required ? (
          <span className="ml-1 text-hb-red-text" aria-hidden="true">
            *
          </span>
        ) : (
          <span className="ml-1.5 text-sm font-normal text-muted-foreground">
            (optional)
          </span>
        )}
        {question.required && <span className="sr-only"> (required)</span>}
      </legend>

      {question.type === "rating" && (
        <RatingInput
          labelId={labelId}
          value={typeof answer === "number" ? answer : undefined}
          disabled={disabled}
          onChange={onChange}
        />
      )}

      {question.type === "single" && (
        <div className="space-y-2">
          {(question.options ?? []).map((option) => (
            <ChoiceRow
              key={option.id}
              type="radio"
              name={`feedback-${question.id}`}
              label={option.label}
              checked={answer === option.id}
              disabled={disabled}
              onChange={() => onChange(option.id)}
            />
          ))}
        </div>
      )}

      {question.type === "multiple" && (
        <div className="space-y-2">
          {(question.options ?? []).map((option) => {
            const picked = Array.isArray(answer) ? answer : [];
            return (
              <ChoiceRow
                key={option.id}
                type="checkbox"
                name={`feedback-${question.id}`}
                label={option.label}
                checked={picked.includes(option.id)}
                disabled={disabled}
                onChange={(checked) => {
                  const next = checked
                    ? [...picked, option.id]
                    : picked.filter((id) => id !== option.id);
                  onChange(next.length > 0 ? next : undefined);
                }}
              />
            );
          })}
        </div>
      )}

      {question.type === "short_text" && (
        <TextAnswer
          multiline={false}
          labelId={labelId}
          max={LIMITS.shortText}
          value={typeof answer === "string" ? answer : ""}
          disabled={disabled}
          onChange={onChange}
          testId={`feedback-text-${number}`}
        />
      )}

      {question.type === "long_text" && (
        <TextAnswer
          multiline
          labelId={labelId}
          max={LIMITS.longText}
          value={typeof answer === "string" ? answer : ""}
          disabled={disabled}
          onChange={onChange}
          testId={`feedback-text-${number}`}
        />
      )}

      {error && (
        <p id={errorId} className="text-sm font-medium text-hb-red-text">
          {error}
        </p>
      )}
    </fieldset>
  );
}

function RatingInput({
  labelId,
  value,
  disabled,
  onChange,
}: {
  labelId: string;
  value: number | undefined;
  disabled: boolean;
  onChange: (answer: number) => void;
}) {
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  // Roving focus, as a radio group behaves: one tab stop, arrows move.
  const focusIndex = value ? value - 1 : 0;

  return (
    <div className="min-w-0">
      <div
        role="radiogroup"
        aria-labelledby={labelId}
        className="flex flex-wrap gap-2"
      >
        {[1, 2, 3, 4, 5].map((rating, index) => {
          const checked = value === rating;
          return (
            <button
              key={rating}
              ref={(element) => {
                buttons.current[index] = element;
              }}
              type="button"
              role="radio"
              aria-checked={checked}
              aria-label={`${rating} of 5`}
              tabIndex={index === focusIndex ? 0 : -1}
              disabled={disabled}
              onClick={() => onChange(rating)}
              onKeyDown={(event) => {
                const step =
                  event.key === "ArrowRight" || event.key === "ArrowDown"
                    ? 1
                    : event.key === "ArrowLeft" || event.key === "ArrowUp"
                      ? -1
                      : 0;
                if (step === 0) return;
                event.preventDefault();
                const next = Math.min(4, Math.max(0, index + step));
                onChange(next + 1);
                buttons.current[next]?.focus();
              }}
              className={cn(
                "flex h-11 w-11 items-center justify-center rounded-md border text-base font-semibold transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                checked
                  ? "border-transparent bg-dusk-tile-a text-white dark:border-edge dark:border-lit-edge dark:shadow-lit"
                  : "border-input bg-background text-foreground hover:bg-muted",
              )}
            >
              {rating}
            </button>
          );
        })}
      </div>
      <p className="mt-1.5 flex max-w-60 justify-between text-xs text-muted-foreground">
        <span>1 = poor</span>
        <span>5 = excellent</span>
      </p>
    </div>
  );
}

function ChoiceRow({
  type,
  name,
  label,
  checked,
  disabled,
  onChange,
}: {
  type: "radio" | "checkbox";
  name: string;
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label
      className={cn(
        "flex min-h-11 min-w-0 cursor-pointer items-center gap-3 rounded-md border px-3 py-2 text-sm transition-colors",
        checked ? "border-lit bg-lit/10" : "border-input bg-background",
        disabled && "cursor-not-allowed opacity-60",
      )}
    >
      <input
        type={type}
        name={name}
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="h-5 w-5 shrink-0 accent-lit"
      />
      <span className="min-w-0 wrap-anywhere text-foreground">{label}</span>
    </label>
  );
}

function TextAnswer({
  multiline,
  labelId,
  max,
  value,
  disabled,
  onChange,
  testId,
}: {
  multiline: boolean;
  labelId: string;
  max: number;
  value: string;
  disabled: boolean;
  onChange: (answer: string | undefined) => void;
  testId: string;
}) {
  const update = (text: string) => onChange(text === "" ? undefined : text);
  return (
    <div className="min-w-0 space-y-1">
      {multiline ? (
        <Textarea
          aria-labelledby={labelId}
          value={value}
          maxLength={max}
          rows={5}
          disabled={disabled}
          onChange={(event) => update(event.target.value)}
          data-testid={testId}
        />
      ) : (
        <Input
          aria-labelledby={labelId}
          value={value}
          maxLength={max}
          disabled={disabled}
          onChange={(event) => update(event.target.value)}
          testId={testId}
        />
      )}
      <p className="text-right text-xs text-muted-foreground">
        {value.length}/{max}
      </p>
    </div>
  );
}
